/**
 * Save to server, discard, and soft-leave session sync for /schedule/tasks.
 * Does not own schedule fetch or the DHTMLX grid.
 */
import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { notifications } from '@mantine/notifications';
import {
  addNewTask,
  createNewProject,
  createTaskDependency,
  deleteTask,
  deleteTaskDependency,
  updateTasks,
  type ScheduleRecalculateEngine,
} from '../../../api/schedulingApis';
import { refreshBaselineRevision } from '../../../api/baselineApis';
import type { ITaskStore } from '../../../stores/ITaskStore';
import {
  dependencyTypeFromDhtmlx,
  transformTaskSnapshotToPayload,
} from '../../../utils/utils';
import {
  mergeCreatePayloadWithSnapshot,
  sanitizeCreateTaskPayload,
} from '../../../gantt/addTaskUtils';
import { filterUpdateIdsExcludingPending } from '../../../gantt/filterUpdateIdsExcludingPending';
import {
  GANTT_SAVE_PARALLEL_CHUNK_SIZE,
  mapInChunks,
} from '../../../gantt/saveBatchUtils';
import { clearGanttSessionSnapshot, saveGanttSessionSnapshot } from '../../../gantt/ganttSessionSnapshot';
import { markGanttPerf, measureGanttPerf } from '../../../gantt/ganttPerfGuards';
import type { ScheduleRecalcEnginePreference } from '../../../gantt/scheduleRecalcEnginePreference';
import { yieldForPaint } from '../../../utils/deferAfterPaint';

type ReloadSchedule = (opts: {
  reason:
    | 'initial'
    | 'invalidate-fetch'
    | 'discard'
    | 'server-recalc'
    | 'baseline-rewrite'
    | 'save-uncertain'
    | 'post-save';
  invalidateLocalCache?: boolean;
}) => Promise<void>;

export function useGanttSessionSync(params: {
  store: ITaskStore;
  projectId: string;
  onConfirmLeaveRef: MutableRefObject<() => Promise<void>>;
  canMutateTasks: boolean;
  activeRevision: { id: string; status: string } | null;
  refreshActiveRevision: () => Promise<unknown>;
  scheduleBackgroundPersist: (reason: string, patchedTaskIds?: string[]) => void;
  reloadScheduleFromServer: ReloadSchedule;
  setLoadingProgress: (message: string) => void;
  recalcEngine: ScheduleRecalcEnginePreference;
  handleServerScheduleRecalculate: (engine: ScheduleRecalculateEngine) => Promise<void>;
}) {
  const {
    store,
    projectId,
    onConfirmLeaveRef,
    canMutateTasks,
    activeRevision,
    refreshActiveRevision,
    scheduleBackgroundPersist,
    reloadScheduleFromServer,
    setLoadingProgress,
    recalcEngine,
    handleServerScheduleRecalculate,
  } = params;

  const [saving, setSaving] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [localProcessing, setLocalProcessing] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const localProcessingCountRef = useRef(0);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);

  const beginLocalProcessing = useCallback(async (title: string, message: string) => {
    localProcessingCountRef.current += 1;
    setLocalProcessing({ title, message });
    if (localProcessingCountRef.current === 1) {
      await yieldForPaint();
    }
  }, []);

  const endLocalProcessing = useCallback(() => {
    localProcessingCountRef.current = Math.max(0, localProcessingCountRef.current - 1);
    if (localProcessingCountRef.current === 0) {
      setLocalProcessing(null);
    }
  }, []);

  /** Soft-leave: keep local edits in IDB + session snapshot so return visits can resume Save to Server. */
  const preserveGanttEditsOnLeave = useCallback(async () => {
    if (!projectId) return;
    const meta = store.getSessionMetadata();
    if (meta.taskCount + meta.linkCount <= 0) return;
    await store.persist(projectId);
    saveGanttSessionSnapshot(projectId, store.exportSessionSnapshot());
  }, [projectId, store]);

  useEffect(() => {
    onConfirmLeaveRef.current = async () => {
      await preserveGanttEditsOnLeave();
    };
  }, [onConfirmLeaveRef, preserveGanttEditsOnLeave]);

  useEffect(() => {
    return () => {
      void preserveGanttEditsOnLeave();
    };
  }, [preserveGanttEditsOnLeave]);

  const handleSaveToBackend = useCallback(async (): Promise<boolean> => {
    if (!canMutateTasks) {
      notifications.show({
        color: 'red',
        title: 'Permission denied',
        message: 'You do not have permission to save schedule changes.',
      });
      return false;
    }
    if (saving) return false;
    try {
      setSaving(true);
      setLoadingProgress('Saving changes to server...');
      markGanttPerf('saveApis');
      if (!projectId) {
        notifications.show({
          title: 'Error',
          message: 'No project ID found',
          color: 'red',
        });
        return false;
      }

      const pendingDeleteIds = store.getPendingDeleteIds();
      const pendingCreates = store.getPendingCreates();
      const pendingCreateIds = new Set(Object.keys(pendingCreates));
      const pendingProject = store.getPendingProjectCreate();
      const affectedTaskIds = store.getAffectedTaskIds();
      const pendingDependencyCreates = store.getPendingDependencyCreates();
      const pendingDependencyDeletes = store.getPendingDependencyDeletes();

      if (pendingProject) {
        await createNewProject(pendingProject, { silent: true });
      }

      // Dependencies first so task deletes do not leave orphaned server links.
      await mapInChunks(
        pendingDependencyDeletes,
        GANTT_SAVE_PARALLEL_CHUNK_SIZE,
        async (dependencyId) => {
          await deleteTaskDependency({ dependencyId }, { silent: true });
        },
      );

      await mapInChunks(
        pendingDeleteIds,
        GANTT_SAVE_PARALLEL_CHUNK_SIZE,
        async (taskId) => {
          await deleteTask({ taskId }, { silent: true });
        },
      );

      // Keep create order by outline level (hierarchy).
      const sortedCreates = Object.entries(pendingCreates).sort(
        ([, a], [, b]) => Number(a.outlineLevel ?? 0) - Number(b.outlineLevel ?? 0),
      );

      for (const [taskId, basePayload] of sortedCreates) {
        const snapshot = store.getTask(taskId);
        const merged = snapshot
          ? mergeCreatePayloadWithSnapshot(basePayload, snapshot)
          : basePayload;
        const payload = sanitizeCreateTaskPayload({
          ...merged,
          taskName: merged.name,
        });
        await addNewTask(payload, { silent: true });
      }

      // Create dependencies after tasks exist (covers new-task + predecessor in one session).
      const deletedTaskIdSet = new Set(pendingDeleteIds.map(String));
      const dependencyCreatesToSend = pendingDependencyCreates.filter((link) => {
        const sourceId = String(link.source);
        const targetId = String(link.target);
        return !deletedTaskIdSet.has(sourceId) && !deletedTaskIdSet.has(targetId);
      });
      const createdDependencyIds: string[] = [];
      await mapInChunks(
        dependencyCreatesToSend,
        GANTT_SAVE_PARALLEL_CHUNK_SIZE,
        async (link) => {
          // Never send numeric gantt/client link ids as DB PKs — UUID TEXT column;
          // omit id so the server uses gen_random_uuid().
          await createTaskDependency(
            {
              projectId,
              taskId: String(link.target),
              dependsOnTaskId: String(link.source),
              dependencyType: dependencyTypeFromDhtmlx(String(link.type ?? '0')),
              lag: link.lag ?? 0,
              meta: {},
            },
            { silent: true },
          );
          createdDependencyIds.push(String(link.id));
        },
      );

      const updateIds = filterUpdateIdsExcludingPending(
        affectedTaskIds,
        pendingCreateIds,
        pendingDeleteIds,
      );
      const allTasks = store.getTasks();
      const taskById = new Map(allTasks.map((task) => [task.id, task]));
      const changedTasks = updateIds
        .map((id) => taskById.get(id))
        .filter((task): task is NonNullable<typeof task> => Boolean(task));
      const edited = store.getEditedTaskFieldsMap();
      const tasksPayload = changedTasks.map((task) => {
        const payload = transformTaskSnapshotToPayload(task);
        if (edited[task.id]?.includes('notes')) {
          // Prefer live map entry over getTasks() cache (field-only patch can leave sorted refs stale).
          const latestNotes = store.getTask(task.id)?.notes ?? task.notes;
          payload.notes = latestNotes ?? '';
        }
        return payload;
      });

      // Dependencies were persisted via dedicated CRUD; only send task field updates here.
      if (tasksPayload.length > 0) {
        await updateTasks({
          affectedTasks: tasksPayload,
          dependencies: [],
        });
      }

      measureGanttPerf('saveApis');
      const hadCreates = pendingCreateIds.size > 0;
      store.clearSessionChanges();
      clearGanttSessionSnapshot(projectId);
      // After creates, refetch so authoritative flat sr_no (and dependency UUIDs) replace provisional.
      if (hadCreates) {
        setLoadingProgress('Refreshing schedule numbers…');
        await reloadScheduleFromServer({ reason: 'post-save' });
      } else {
        // Local rows were incrementally persisted during edits; skip a full 15k IDB rewrite.
        scheduleBackgroundPersist('post-save', []);
      }

      const parts: string[] = [];
      if (pendingProject) {
        parts.push('project initialized');
      }
      if (pendingDeleteIds.length > 0) {
        parts.push(`${pendingDeleteIds.length} deleted`);
      }
      if (pendingCreateIds.size > 0) {
        parts.push(`${pendingCreateIds.size} created`);
      }
      if (tasksPayload.length > 0) {
        parts.push(`${tasksPayload.length} updated`);
      }
      if (pendingDependencyDeletes.length > 0 || createdDependencyIds.length > 0) {
        parts.push(
          `${pendingDependencyDeletes.length + createdDependencyIds.length} link(s)`,
        );
      }
      const changeMsg =
        parts.length > 0
          ? `Saved to server: ${parts.join(', ')}.`
          : 'Saved to server.';
      notifications.show({
        title: 'Success',
        message: changeMsg,
        color: 'teal',
      });

      // Keep open draft revision snapshot in sync with the schedule just saved.
      if (
        activeRevision &&
        (activeRevision.status === 'DRAFT' || activeRevision.status === 'REJECTED')
      ) {
        try {
          await refreshBaselineRevision(
            { revisionId: activeRevision.id },
            { silent: true },
          );
          await refreshActiveRevision();
        } catch (refreshErr) {
          console.error(
            '[Save] Schedule saved, but refreshing open baseline revision snapshot failed:',
            refreshErr,
          );
        }
      }

      if (recalcEngine === 'MPXJ' || recalcEngine === 'CPM') {
        setLoadingProgress(`Recalculating schedule (${recalcEngine})...`);
        await handleServerScheduleRecalculate(recalcEngine);
      }

      return true;
    } catch (err) {
      console.error('[Save] Failed to save to backend:', err);
      // Error notification already shown by API helpers. Keep local session so user can retry.
      return false;
    } finally {
      setSaving(false);
      setLoadingProgress('');
      store.setLoading(false);
    }
  }, [
    store,
    projectId,
    saving,
    canMutateTasks,
    activeRevision,
    refreshActiveRevision,
    scheduleBackgroundPersist,
    reloadScheduleFromServer,
    recalcEngine,
    handleServerScheduleRecalculate,
  ]);

  const handleConfirmSaveToBackend = useCallback(async () => {
    setSaveConfirmOpen(false);
    await handleSaveToBackend();
  }, [handleSaveToBackend]);

  const handleDiscardChanges = useCallback(async () => {
    if (discarding || saving || !projectId) return;
    try {
      setDiscarding(true);
      setDiscardConfirmOpen(false);
      store.setSelectedTaskId(null);
      store.setLoading(true);
      setLoadingProgress('Discarding changes...');
      await reloadScheduleFromServer({ reason: 'discard' });
      store.clearSessionChanges();
      clearGanttSessionSnapshot(projectId);
      notifications.show({
        title: 'Changes discarded',
        message: 'Your unsaved edits were removed. The schedule was reloaded from the server.',
        color: 'gray',
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to discard changes';
      console.error('[ZustandGantt] Discard changes failed:', err);
      notifications.show({ title: 'Error', message, color: 'red' });
    } finally {
      setDiscarding(false);
      store.setLoading(false);
      setLoadingProgress('');
    }
  }, [discarding, saving, projectId, store, reloadScheduleFromServer]);

  return {
    saving,
    setSaving,
    discarding,
    setDiscarding,
    localProcessing,
    discardConfirmOpen,
    setDiscardConfirmOpen,
    saveConfirmOpen,
    setSaveConfirmOpen,
    beginLocalProcessing,
    endLocalProcessing,
    handleSaveToBackend,
    handleConfirmSaveToBackend,
    handleDiscardChanges,
  };
}
